import React, { useEffect, useRef } from "react";
import { Helper } from "@utils/index";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, Maximize, Minimize } from "lucide-react";
import styled from "@emotion/styled";

const FullPageOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at 80% 20%, rgba(255, 49, 53, 0.05) 0%, transparent 40%),
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 40%);
    animation: rotate 20s linear infinite;
    pointer-events: none;
  }

  @keyframes rotate {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const VideoGrid = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const RemoteVideoContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  max-width: 1400px;
  max-height: 800px;
  background: #111827;
  border-radius: 32px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.05);

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LocalVideoContainer = styled(motion.div)`
  position: absolute;
  top: 40px;
  right: 40px;
  width: 280px;
  aspect-ratio: 16/9;
  background: #1e293b;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
  cursor: grab;

  &:active { cursor: grabbing; }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scaleX(-1);
  }
`;

const ControlsContainer = styled(motion.div)`
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: fit-content;
  display: flex;
  gap: 20px;
  padding: 16px 32px;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(24px) saturate(180%);
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const ControlButton = styled.button<{ variant?: 'danger' | 'success' | 'ghost', active?: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  background: ${props => {
    if (props.variant === 'danger') return '#ff3135'; // Project Red
    if (props.variant === 'success') return '#10b981';
    return props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)';
  }};
  color: white;
  transition: all 0.3s cubic-bezier(0.2, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    background: ${props => {
      if (props.variant === 'danger') return '#e02d31';
      if (props.variant === 'success') return '#059669';
      return props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)';
    }};
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }

  &:active { transform: translateY(0); }

  svg { width: 24px; height: 24px; }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 24px;
  left: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  z-index: 20;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ff3135;
    box-shadow: 0 0 10px #ff3135;
    animation: flash 1.5s infinite;
  }

  @keyframes flash {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

const IncomingOverlay = styled(motion.div)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 100;

  .avatar-ring {
    position: relative;
    width: 140px;
    height: 140px;
    margin-bottom: 32px;
    
    .ring {
      position: absolute;
      inset: -10px;
      border-radius: 50%;
      border: 2px solid #ff3135;
      animation: pulse-ring 2s infinite;
    }

    .avatar {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 48px;
      font-weight: 700;
      border: 2px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    }
  }

  @keyframes pulse-ring {
    0% { transform: scale(0.9); opacity: 0.8; }
    100% { transform: scale(1.4); opacity: 0; }
  }

  h2 { font-size: 36px; font-weight: 800; margin-bottom: 8px; letter-spacing: -0.5px; }
  p { font-size: 18px; color: #94a3b8; margin-bottom: 48px; }
`;

interface Props {
  state: any;
  onAccept: () => void;
  onReject: () => void;
  onEnd: () => void;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
}

const CallOverlay: React.FC<Props> = ({ state, onAccept, onReject, onEnd, onToggleAudio, onToggleVideo }) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (localVideoRef.current && state.localStream) localVideoRef.current.srcObject = state.localStream;
  }, [state.localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && state.remoteStream) remoteVideoRef.current.srcObject = state.remoteStream;
  }, [state.remoteStream]);

  const content = (
    <AnimatePresence>
      <FullPageOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        {state.incoming && state.status === "idle" ? (
          <IncomingOverlay>
            <div className="avatar-ring">
              <div className="ring" />
              <div className="avatar">{state.callerName?.charAt(0) || "U"}</div>
            </div>
            <h2>{state.callerName}</h2>
            <p>Incoming {state.type} call...</p>
            <div style={{ display: 'flex', gap: '32px' }}>
              <ControlButton variant="success" onClick={onAccept}>
                <Phone />
              </ControlButton>
              <ControlButton variant="danger" onClick={onReject}>
                <PhoneOff />
              </ControlButton>
            </div>
          </IncomingOverlay>
        ) : (
          <VideoGrid>
            <AnimatePresence>
              {state.type === "video" && (
                <LocalVideoContainer
                  drag
                  dragConstraints={{ left: -400, right: 400, top: -300, bottom: 500 }}
                  initial={{ opacity: 0, scale: 0.8, x: 20 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  transition={{ type: 'spring', damping: 20 }}
                >
                  <video ref={localVideoRef} autoPlay muted playsInline />
                  {state.isVideoOff && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyItems: 'center', background: '#0f172a', zIndex: 10 }}>
                        <div style={{ margin: 'auto' }}><VideoOff size={32} opacity={0.5} /></div>
                    </div>
                  )}
                </LocalVideoContainer>
              )}
            </AnimatePresence>

            <RemoteVideoContainer initial={{ scale: 0.95 }} animate={{ scale: 1 }}>
              <StatusBadge>
                <div className="dot" />
                {state.status === "connected" 
                  ? `LIVE • ${Helper.formatDuration(state.duration || 0)}` 
                  : state.status.toUpperCase()}
              </StatusBadge>
              
              {state.status === "connected" ? (
                state.type === "video" ? (
                  <video ref={remoteVideoRef} autoPlay playsInline />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111827' }}>
                    <div className="avatar" style={{ width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', fontWeight: 'bold' }}>
                      {state.callerName?.charAt(0) || "P"}
                    </div>
                  </div>
                )
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#111827' }}>
                  <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', animation: 'pulse 2s infinite' }}>
                    {state.callerName?.charAt(0) || "P"}
                  </div>
                  <h3 style={{ marginTop: '24px', letterSpacing: '4px', color: '#64748b', fontSize: '14px', fontWeight: '700' }}>
                    {state.status.toUpperCase()}...
                  </h3>
                </div>
              )}
              
              <div style={{ position: 'absolute', bottom: '24px', left: '24px', fontSize: '14px', fontWeight: '600', opacity: 0.8 }}>
                {state.callerName || "Participant"}
              </div>
            </RemoteVideoContainer>

            <ControlsContainer 
              initial={{ y: 100, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <ControlButton onClick={onToggleAudio} active={state.isAudioMuted}>
                {state.isAudioMuted ? <MicOff /> : <Mic />}
              </ControlButton>
              {state.type === "video" && (
                <ControlButton onClick={onToggleVideo} active={state.isVideoOff}>
                  {state.isVideoOff ? <VideoOff /> : <Video />}
                </ControlButton>
              )}
              <div style={{ width: '1px', height: '32px', background: 'rgba(255,255,255,0.1)', margin: 'auto 10px' }} />
              <ControlButton variant="danger" onClick={onEnd}>
                <PhoneOff />
              </ControlButton>
            </ControlsContainer>
          </VideoGrid>
        )}
      </FullPageOverlay>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default CallOverlay;
